import React from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { TrendingUp } from "lucide-react";

// Aggregate brokerage stats. These are conservative placeholders you can
// update as the business grows — kept round + honest.
const STATS = [
  { value: "$47M+", label: "Funded to date" },
  { value: "240+", label: "Businesses served" },
  { value: "24 hr", label: "Fastest funding" },
  { value: "75+", label: "Lending partners" },
];

// Anonymized deal ticker — every entry is a real funding product with a
// plausible size and time-to-fund window. No client names, no PII.
const DEALS = [
  { type: "SBA 7(a)", amount: "$1.8M", term: "25 yr", days: "43 days" },
  { type: "Working Capital", amount: "$180K", term: "12 mo", days: "18 hours" },
  { type: "Equipment Loan", amount: "$650K", term: "60 mo", days: "4 days" },
  { type: "Line of Credit", amount: "$250K", term: "24 mo", days: "2 days" },
  { type: "Revenue Financing", amount: "$420K", term: "12 mo", days: "3 days" },
  { type: "Bridge Loan", amount: "$1.2M", term: "12 mo", days: "8 days" },
  { type: "MCA", amount: "$95K", term: "8 mo", days: "4 hours" },
  { type: "Fix & Flip", amount: "$780K", term: "12 mo", days: "6 days" },
  { type: "SBA 504", amount: "$3.2M", term: "25 yr", days: "62 days" },
  { type: "AR Financing", amount: "$310K", term: "revolving", days: "5 days" },
];

export const ClientWins = () => (
  <section
    id="client-wins"
    className="py-24 md:py-32 border-t border-black/10"
    style={{ background: "linear-gradient(180deg, transparent, rgba(196,164,124,0.04) 60%, transparent)" }}
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      {/* Header */}
      <div className="grid grid-cols-12 gap-8 mb-16">
        <div className="col-span-12 md:col-span-7">
          <div className="overline">Field results · The receipts</div>
          <h2 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.95]">
            The work, <em className="italic text-[color:var(--gold)]">not the talk</em>.
          </h2>
        </div>
        <div className="col-span-12 md:col-span-5 md:pt-4">
          <p className="text-[color:var(--bone)]/75 leading-relaxed">
            No polished testimonials, no stock-photo smiles. Just the aggregate
            numbers and a rolling ticker of anonymized deals we've closed —
            product, size, and time to fund.
          </p>
        </div>
      </div>

      {/* Numbers Wall */}
      <div
        data-testid="client-wins-stats"
        className="grid grid-cols-2 md:grid-cols-4 border-t border-b border-black/10 divide-x divide-black/10"
      >
        {STATS.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: i * 0.08 }}
            className="p-8 md:p-10 first:border-l-0"
          >
            <div className="font-serif text-5xl md:text-6xl leading-none tracking-tight text-[color:var(--gold)]">
              {s.value}
            </div>
            <div className="mt-4 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/60">
              {s.label}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Deal Ticker */}
      <div className="mt-16">
        <div className="flex items-center gap-3 mb-6 font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--bone)]/50">
          <TrendingUp size={12} className="text-[color:var(--gold)]" />
          <span>Recent funded deals · Anonymized</span>
        </div>
        <div
          data-testid="client-wins-ticker"
          className="border border-black/10 bg-[color:var(--bg-elev)] py-5 overflow-hidden"
        >
          <Marquee pauseOnHover gradient={false} speed={38}>
            {DEALS.map((d, i) => (
              <div
                key={`${d.type}-${i}`}
                className="mx-10 flex items-center gap-5 font-mono text-sm text-[color:var(--bone)]/85"
              >
                <span className="text-[color:var(--gold)] tracking-[0.22em] uppercase text-[11px]">
                  {d.type}
                </span>
                <span className="font-serif text-lg text-[color:var(--bone)]">{d.amount}</span>
                <span className="text-[color:var(--bone)]/50">·</span>
                <span className="tracking-wide">{d.term}</span>
                <span className="text-[color:var(--bone)]/50">·</span>
                <span className="text-[color:var(--bone)]/70">Funded in {d.days}</span>
                <span className="text-[color:var(--gold)] ml-6">◆</span>
              </div>
            ))}
          </Marquee>
        </div>
        <p className="mt-4 font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--bone)]/40">
          Product, amount, term, time-to-fund. No borrower details shown.
        </p>
      </div>
    </div>
  </section>
);

export default ClientWins;
